import { Component, HostBinding, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { AppInfoService, ScreenService, ThemeService } from './services';
import { locale } from 'devextreme/localization';
import config from 'devextreme/core/config';
import { licenseKey } from 'src/devextreme-license';
import { MsalService } from '@azure/msal-angular';
import { AuthPermissionService } from './services/auth/permission.service';
import { AuthService } from './services/auth/auth.service';
import { EmployeeService } from './services/employee/employee.service';
import { ImpersonationService } from './services/impersonation/impersonation.service';
import { environment } from 'src/environments/environment';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AppComponent implements OnDestroy {
  @HostBinding('class') get getClass() {
    return Object.keys(this.screen.sizes)
      .filter((cl) => this.screen.sizes[cl])
      .join(' ');
  }

  // Reserves space at the top for the fixed impersonation banner.
  @HostBinding('class.impersonating') get isImpersonating() {
    return this.impersonation.isActive();
  }

  constructor(
    themeService: ThemeService,
    private screen: ScreenService,
    private authService: AuthService,
    private authPermissionService: AuthPermissionService,
    private employeeSvc: EmployeeService,
    private msalService: MsalService,
    public appInfo: AppInfoService,
    public impersonation: ImpersonationService
  ) {
    locale(navigator.language);
    config({ defaultCurrency: 'EUR', licenseKey });
    themeService.setAppTheme();
    this.loadPermissions();
  }

  loadPermissions() {
    // Local dev bypass: no Azure AD account exists, so grant admin directly instead of
    // reading MSAL claims / calling the permission endpoint.
    if (environment.authBypass) {
      this.authPermissionService.grantDevAdmin();
      return;
    }

    const permissionsNeeded = !this.authPermissionService.hasPermissionsInLocalStore();
    const employeeNeeded = !this.employeeSvc.hasEmployeeInLocalStore();
    if (!permissionsNeeded && !employeeNeeded) return;

    this.msalService.handleRedirectObservable().subscribe(() => {
      const account = this.msalService.instance.getAllAccounts()[0];
      if (!account) return;

      if (permissionsNeeded) {
        this.authPermissionService.loadPermissions(account.idTokenClaims['oid']);
      }
      if (employeeNeeded) {
        this.employeeSvc.loadEmployee();
      }
      this.authService.getUser();
    });
  }

  isAuthenticated() {
    return this.authService.loggedIn;
  }

  ngOnDestroy(): void {
    this.screen.breakpointSubscription.unsubscribe();
  }
}
